import { Plan, PlanPurchaseExperienceMode } from '@dtx-company/true-common/src/types/Plan'
import { PlanPurchaseExperienceProps } from '../types'
import { getPlans } from '@dtx-company/inter-app/src/api/subscription-service/getPlans'
import { useAPISWR } from '@dtx-company/inter-app/src/hooks/useAPISWR/useAPISWR'
import { useEffect, useState } from 'react'
import { usePlanPurchaseExperiencePricingGroup } from '@app/common/src/components/PlanPurchaseExperience/hooks/usePlanPurchaseExperiencePricingGroup'
import { usePlanPurchaseExperienceSubscriptionInfo } from '@app/common/src/components/PlanPurchaseExperience/hooks/usePlanPurchaseExperienceSubscriptionInfo'

export function usePlanPurchaseExperiencePlans(
  props: PlanPurchaseExperienceProps
): Plan[] | undefined {
  const group = usePlanPurchaseExperiencePricingGroup(props)
  const { currentSubscriptionInfoSWR, currentSubscriptionInfoData } =
    usePlanPurchaseExperienceSubscriptionInfo()
  const currentPlansSWR = useAPISWR(
    getPlans,
    () => {
      if (!group) return
      if (currentSubscriptionInfoSWR.isLoading) return
      return { filter: { groupEq: group } }
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false
    }
  )

  const [availablePlans, setAvailablePlans] = useState<Plan[] | undefined>(undefined)

  useEffect(() => {
    if (props.mode === PlanPurchaseExperienceMode.PURCHASE_ADDONS) {
      if (currentSubscriptionInfoData?.plan) {
        setAvailablePlans([currentSubscriptionInfoData.plan])
      } else {
        setAvailablePlans([])
      }
    } else if (!currentPlansSWR.data) {
      setAvailablePlans([])
    } else {
      setAvailablePlans(
        currentPlansSWR.data
          .filter(plan => {
            if (!props.planType) return true
            if (plan.type === props.planType) return true
          })
          // The API was not always consistent in its ordering so adding a sort here
          // to provide a consistent experience
          .sort((planA, planB) => {
            // Sort plan type by name (doesn't really matter the order since we only
            // see one plan type at a time)
            const typeCompare: number = planA.type.localeCompare(planB.type)
            if (typeCompare !== 0) return typeCompare
            // If plan types match, sort yearly first (using an alphabetical sort for simplicity)
            if (planA.interval && planB.interval) {
              return -1 * planA.interval.localeCompare(planB.interval)
            }
            // Fallback to id sort to ensure consistency
            return planA.id.localeCompare(planB.id)
          })
      )
    }
  }, [props.mode, currentSubscriptionInfoData, currentPlansSWR.data, props.planType])

  return availablePlans
}
